// DrawerButton.tsx - يقرأ من localStorage (النظام الجديد)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loadLocalDeviceSettings } from './localPrinterSettings';

interface DrawerButtonProps {
  onOpen?: () => void;
}

interface DrawerSettings {
  enabled: boolean;
  shortcut_key: string;
  printer_name: string;
}

function DrawerButton({ onOpen }: DrawerButtonProps) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<DrawerSettings>({ 
    enabled: false, 
    shortcut_key: 'F8',
    printer_name: ''
  });
  const [initialLoading, setInitialLoading] = useState(true);
  
  // استخدام ref للقيم الحديثة
  const settingsRef = useRef(settings);

  // تحديث ref عند تغيير settings
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // ✅ دالة عرض الرسائل (حطيناها جوه الكومبوننت)
  const showMessage = useCallback((text: string, type: 'success' | 'error') => {
    alert(text); // مؤقتاً هنستخدم alert
    console.log(`💰 ${type}: ${text}`);
  }, []);

  // ✅ تحميل الإعدادات من localStorage (النظام الجديد)
  const loadSettings = useCallback(() => {
    try {
      const deviceSettings = loadLocalDeviceSettings();
      setSettings({
        enabled: deviceSettings.drawer.enabled,
        shortcut_key: deviceSettings.drawer.shortcut_key,
        printer_name: deviceSettings.drawer.printer_name
      });
      console.log('💰 DrawerButton: تم تحميل الإعدادات:', deviceSettings.drawer);
    } catch (error) {
      console.error('💰 DrawerButton: خطأ في تحميل الإعدادات:', error);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  // ✅ دالة فتح الدرج - تستخدم الوكيل المحلي
  const openDrawer = useCallback(async () => {
    if (!settingsRef.current.enabled) {
      alert('⚠️ الدرج غير مفعل');
      return;
    }
    
    if (!settingsRef.current.printer_name) {
      alert('⚠️ لم يتم اختيار طابعة');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('💰 محاولة فتح الدرج على:', settingsRef.current.printer_name);
      
      // ✅ نجرب الأمر ده (Xprinter)
      const response = await fetch('http://localhost:5000/drawer/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printer_name: settingsRef.current.printer_name,
          command_hex: '1B70003264'  // أمر Xprinter
        }),
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('💰 رد الوكيل:', data);
        
        if (data.success) {
          if (onOpen) onOpen();
          window.dispatchEvent(new CustomEvent('drawer-opened'));
          showMessage('✅ تم فتح الدرج بنجاح', 'success');
        } else {
          showMessage(`❌ ${data.message || 'فشل فتح الدرج'}`, 'error');
        }
      } else {
        const errorText = await response.text();
        console.error('💰 فشل:', response.status, errorText);
        showMessage(`❌ فشل الاتصال بالوكيل المحلي (${response.status})`, 'error');
      }
    } catch (error: any) {
      console.error('💰 DrawerButton: خطأ:', error);
      showMessage(`❌ ${error.message || 'حدث خطأ في فتح الدرج'}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [settingsRef, onOpen, showMessage]);

  // الاستماع لتغييرات الإعدادات
  useEffect(() => {
    loadSettings();
    
    const handleSettingsChanged = () => {
      console.log('💰 DrawerButton: تم تغيير الإعدادات، إعادة التحميل...');
      loadSettings();
    };
    
    window.addEventListener('drawer-settings-changed', handleSettingsChanged);
    
    return () => {
      window.removeEventListener('drawer-settings-changed', handleSettingsChanged);
    };
  }, [loadSettings]);

  // معالج الضغط على الاختصار
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!settingsRef.current.enabled) return;
      
      const key = settingsRef.current.shortcut_key;
      
      if (key.startsWith('Ctrl+')) {
        const ctrlKey = key.replace('Ctrl+', '');
        if (e.ctrlKey && e.key === ctrlKey) {
          e.preventDefault();
          openDrawer();
        }
      } else if (e.key === key) {
        e.preventDefault();
        openDrawer();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openDrawer]);

  if (initialLoading) {
    return (
      <button
        disabled
        style={{
          padding: '8px 16px',
          backgroundColor: '#ccc',
          color: '#666',
          border: 'none',
          borderRadius: '4px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: '10px',
          opacity: 0.5,
          cursor: 'wait'
        }}
      >
        <span style={{ fontSize: '18px' }}>⏳</span>
        <span>جاري التحميل...</span>
      </button>
    );
  }

  if (!settings.enabled) {
    return null;
  }

  return (
    <button
      onClick={openDrawer}
      disabled={loading}
      style={{
        padding: '8px 16px',
        backgroundColor: loading ? '#ccc' : '#FF9800',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: loading ? 'not-allowed' : 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginLeft: '10px',
        transition: 'all 0.3s ease',
        opacity: loading ? 0.6 : 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundColor = '#F57C00';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundColor = '#FF9800';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }
      }}
      title={`اختصار لوحة المفاتيح: ${settings.shortcut_key}`}
    >
      <span style={{ fontSize: '18px' }}>💵</span>
      <span>{loading ? 'جاري الفتح...' : `فتح الدرج (${settings.shortcut_key})`}</span>
    </button>
  );
}

export default DrawerButton;